import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
    Alert,
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { UsPhoneInput, isValidUsPhone } from "@/components/UsPhoneInput";
import {
    confirmMyPhoneVerify,
    fetchMyPhones,
    removeMyPhone,
    setMyPhonePrimary,
    startMyPhoneVerify,
    type RnPhoneEntry
} from "@/lib/api";
import { colors, fonts, radius, spacing } from "@/lib/theme";

type Mode = { kind: "list" } | { kind: "add" } | { kind: "verify"; number: string };

export default function PhonesScreen() {
    const router = useRouter();
    const [mode, setMode] = useState<Mode>({ kind: "list" });
    const [phones, setPhones] = useState<RnPhoneEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [draftDigits, setDraftDigits] = useState("");
    const [code, setCode] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchMyPhones();
            setPhones(res.phones);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to load phones");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const verifiedCount = phones.filter((p) => p.verifiedAt !== null).length;

    async function handleSendCode() {
        if (!isValidUsPhone(draftDigits) || busy) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await startMyPhoneVerify(`+1${draftDigits}`);
            setMode({ kind: "verify", number: `+1${draftDigits}` });
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to send code");
        } finally {
            setBusy(false);
        }
    }

    async function handleConfirm() {
        if (mode.kind !== "verify" || code.length < 4 || busy) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await confirmMyPhoneVerify(mode.number, code);
            setDraftDigits("");
            setCode("");
            setMode({ kind: "list" });
            await load();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Invalid code");
        } finally {
            setBusy(false);
        }
    }

    async function handleMakePrimary(p: RnPhoneEntry) {
        if (busy) {
            return;
        }
        setBusy(true);
        try {
            await setMyPhonePrimary(p.id);
            await load();
        } catch (e: unknown) {
            Alert.alert("Could not set primary", e instanceof Error ? e.message : "");
        } finally {
            setBusy(false);
        }
    }

    function handleRemove(p: RnPhoneEntry) {
        Alert.alert("Remove this number?", "You won't be able to log in with it anymore.", [
            { text: "Cancel", style: "cancel" },
            {
                text: "Remove",
                style: "destructive",
                onPress: async () => {
                    setBusy(true);
                    try {
                        await removeMyPhone(p.id);
                        await load();
                    } catch (e: unknown) {
                        Alert.alert("Could not remove", e instanceof Error ? e.message : "");
                    } finally {
                        setBusy(false);
                    }
                }
            }
        ]);
    }

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAvoidingView
                style={styles.flex}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                <View style={styles.header}>
                    <TouchableOpacity
                        onPress={() => {
                            if (mode.kind === "list") {
                                router.back();
                            } else {
                                setMode({ kind: "list" });
                                setError(null);
                                setCode("");
                            }
                        }}
                        hitSlop={12}
                    >
                        <MaterialCommunityIcons
                            name="arrow-left"
                            size={22}
                            color={colors.textPrimary}
                        />
                    </TouchableOpacity>
                    <Text style={styles.title}>Phone numbers</Text>
                    <View style={{ width: 22 }} />
                </View>

                <ScrollView contentContainerStyle={styles.content}>
                    {mode.kind === "list" ? (
                        <>
                            {loading ? <Text style={styles.muted}>Loading…</Text> : null}
                            {phones.map((p) => {
                                const verified = p.verifiedAt !== null;
                                const removeBlocked = verified && verifiedCount <= 1;
                                return (
                                    <View key={p.id} style={styles.row}>
                                        <View style={styles.rowLeft}>
                                            <Text style={styles.number}>
                                                {formatDisplay(p.number)}
                                            </Text>
                                            <View style={styles.badges}>
                                                {p.isPrimary ? (
                                                    <Badge text="Primary" tone="brand" />
                                                ) : null}
                                                {verified ? (
                                                    <Badge text="Verified" tone="success" />
                                                ) : (
                                                    <Badge text="Pending" tone="warn" />
                                                )}
                                            </View>
                                        </View>
                                        <View style={styles.rowActions}>
                                            {!p.isPrimary && verified ? (
                                                <TouchableOpacity
                                                    onPress={() => handleMakePrimary(p)}
                                                    disabled={busy}
                                                >
                                                    <Text style={styles.actionLink}>
                                                        Make primary
                                                    </Text>
                                                </TouchableOpacity>
                                            ) : null}
                                            <TouchableOpacity
                                                onPress={() => handleRemove(p)}
                                                disabled={busy || removeBlocked}
                                                hitSlop={8}
                                            >
                                                <MaterialCommunityIcons
                                                    name="close-circle-outline"
                                                    size={20}
                                                    color={
                                                        removeBlocked
                                                            ? colors.textTertiary
                                                            : colors.textSecondary
                                                    }
                                                />
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                );
                            })}
                            <TouchableOpacity
                                style={styles.addButton}
                                onPress={() => {
                                    setMode({ kind: "add" });
                                    setError(null);
                                }}
                            >
                                <MaterialCommunityIcons
                                    name="plus"
                                    size={16}
                                    color={colors.teal400}
                                />
                                <Text style={styles.addButtonText}>Add a phone number</Text>
                            </TouchableOpacity>
                        </>
                    ) : null}

                    {mode.kind === "add" ? (
                        <>
                            <Text style={styles.sectionTitle}>Add a phone number</Text>
                            <Text style={styles.muted}>
                                We'll send a code by SMS. Enter it on the next screen.
                            </Text>
                            <View style={{ height: spacing.lg }} />
                            <UsPhoneInput
                                value={draftDigits}
                                onChange={setDraftDigits}
                                autoFocus
                                error={error}
                            />
                            <View style={{ height: spacing.lg }} />
                            <TouchableOpacity
                                style={[
                                    styles.primaryButton,
                                    (!isValidUsPhone(draftDigits) || busy) &&
                                        styles.primaryButtonDisabled
                                ]}
                                onPress={handleSendCode}
                                disabled={!isValidUsPhone(draftDigits) || busy}
                            >
                                <Text style={styles.primaryButtonText}>
                                    {busy ? "Sending…" : "Send code"}
                                </Text>
                            </TouchableOpacity>
                        </>
                    ) : null}

                    {mode.kind === "verify" ? (
                        <>
                            <Text style={styles.sectionTitle}>Enter the code</Text>
                            <Text style={styles.muted}>
                                We sent a 6-digit code to {formatDisplay(mode.number)}.
                            </Text>
                            <View style={{ height: spacing.lg }} />
                            <TextInput
                                style={styles.codeInput}
                                value={code}
                                onChangeText={(t) => setCode(t.replace(/\D/g, "").slice(0, 6))}
                                keyboardType="number-pad"
                                placeholder="123456"
                                placeholderTextColor={colors.textTertiary}
                                maxLength={6}
                                autoFocus
                            />
                            {error ? <Text style={styles.errorText}>{error}</Text> : null}
                            <View style={{ height: spacing.lg }} />
                            <TouchableOpacity
                                style={[
                                    styles.primaryButton,
                                    (code.length < 4 || busy) && styles.primaryButtonDisabled
                                ]}
                                onPress={handleConfirm}
                                disabled={code.length < 4 || busy}
                            >
                                <Text style={styles.primaryButtonText}>
                                    {busy ? "Verifying…" : "Verify"}
                                </Text>
                            </TouchableOpacity>
                        </>
                    ) : null}
                </ScrollView>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

function formatDisplay(e164: string): string {
    const digits = e164.replace(/\D/g, "").slice(-10);
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function Badge({ text, tone }: { text: string; tone: "brand" | "success" | "warn" }) {
    const bg =
        tone === "brand"
            ? "rgba(69,210,176,0.15)"
            : tone === "success"
              ? "rgba(34,197,94,0.15)"
              : "rgba(251,191,36,0.15)";
    const fg = tone === "brand" ? colors.teal400 : tone === "success" ? "#16A34A" : "#D97706";
    return (
        <View
            style={{
                backgroundColor: bg,
                borderRadius: radius.sm,
                paddingHorizontal: spacing.xs,
                paddingVertical: 2,
                marginRight: spacing.xs
            }}
        >
            <Text style={{ fontFamily: fonts.medium, fontSize: 11, color: fg }}>{text}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    flex: { flex: 1 },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        paddingBottom: spacing.md
    },
    title: {
        fontFamily: fonts.medium,
        fontSize: 16,
        color: colors.textPrimary
    },
    content: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.xl
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: colors.backgroundSecondary,
        borderRadius: radius.md,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.md,
        marginBottom: spacing.sm
    },
    rowLeft: { flex: 1 },
    rowActions: { flexDirection: "row", alignItems: "center", gap: spacing.md as number },
    number: {
        fontFamily: fonts.medium,
        fontSize: 15,
        color: colors.textPrimary
    },
    badges: { flexDirection: "row", marginTop: spacing.xs },
    actionLink: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.teal400
    },
    addButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs as number,
        paddingVertical: spacing.md
    },
    addButtonText: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.teal400,
        marginLeft: spacing.xs
    },
    sectionTitle: {
        fontFamily: fonts.medium,
        fontSize: 18,
        color: colors.textPrimary,
        marginBottom: spacing.xs
    },
    muted: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary
    },
    codeInput: {
        fontFamily: fonts.regular,
        fontSize: 24,
        color: colors.textPrimary,
        textAlign: "center",
        letterSpacing: 8,
        paddingVertical: spacing.md,
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        borderRadius: radius.md,
        backgroundColor: colors.backgroundSecondary
    },
    errorText: {
        marginTop: spacing.xs,
        fontFamily: fonts.regular,
        fontSize: 12,
        color: colors.error500
    },
    primaryButton: {
        backgroundColor: colors.teal400,
        borderRadius: radius.md,
        paddingVertical: spacing.md,
        alignItems: "center"
    },
    primaryButtonDisabled: {
        opacity: 0.5
    },
    primaryButtonText: {
        fontFamily: fonts.medium,
        fontSize: 15,
        color: "#FFFFFF"
    }
});
